import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { AppSearch, AppSearchHit } from "./AppSearch";
import { AppSearchActOpts, AppSearchActRules } from "./AppSearchActions";
import { IComposerSent } from "./AppComposer";
import { IRxPending } from "./AppReactPoll";
import { IAppTaskHeard } from "./AppTaskActions";
import { AppRow } from "./AppIndex";
import { IAssistCopied } from "./AppAssistPanel";

// AppSearchActRun - the click side of a search hit's actions (`AppSearchActions` draws them). ONE delegated click on the
// hits element hands the pressed button here; the page does the act (`IAppSearchActHost`, `AppMain`).
//
// ⛔ DECISION 9: only a click on one of the six FACES reaches a sender (`Host.React` -> `AppMain.QueueReaction`, the
// page-wide ticket). Reply / Forward / Help me reply open things; Remind me writes a task; Copy copies. One reaction in
// flight per hit; a press while it is on its way does nothing.

/** A hit's element, as much of the DOM as this file needs (tests hand in fakes). */
export interface ISaEl {
    getAttribute(Name: string): string | null;
    setAttribute(Name: string, Value: string): void;
    closest(Sel: string): ISaEl | null;
    querySelector(Sel: string): ISaEl | null;
    textContent: string | null;
    hidden: boolean;
}

/** The page's half. Each answers "" when it went, else the sentence to show on the hit. */
export interface IAppSearchActHost {
    Reply(Hit: AppSearchHit): void;
    Ai(Hit: AppSearchHit): void;
    Forward(Hit: AppSearchHit): void;
    React(Hit: AppSearchHit, Emoji: string, Then: IComposerSent, Watch: IRxPending): string;
    Remind(Hit: AppSearchHit, Heard: IAppTaskHeard): string;
    Copy(Text: string, Then: IAssistCopied): void;
    /** What the page can do right now (send, AI, tasks, the contacts' pictures). */
    Opts(): AppSearchActOpts;
    /** The hits were drawn into `El` (the avatar discs are filled from here). */
    Painted(El: unknown): void;
}

export class AppSearchActRun {
    private Host: IAppSearchActHost;
    /** The page's hits, in `data-sa-hit` order (`AppSearchActView.Groups` fills it). */
    public Hits: AppSearchHit[] = [];
    /** What the page could do when it drew them. */
    public Opts: AppSearchActOpts = new AppSearchActOpts();
    /** Hits (by message id) whose reaction is on its way. */
    private Reacting: Record<string, boolean> = {};
    private Reminding: Record<string, boolean> = {};

    public constructor(Host: IAppSearchActHost) {
        this.Host = Host;
    }

    /** A click inside the hits. True when it was one of ours (the caller then stops). */
    public OnClick = (Target: ISaEl): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            const Button: ISaEl | null = Target.closest("[" + AppSearchActRules.ActAttr + "]");
            if (Button === null) { return false; }
            const Li: ISaEl | null = Button.closest("[" + AppSearchActRules.HitAttr + "]");
            if (Li === null) { return true; }
            const At: number = Number(Li.getAttribute(AppSearchActRules.HitAttr));
            if (isFinite(At) === false || At < 0 || At >= this.Hits.length) { return true; }
            const Hit: AppSearchHit = this.Hits[At];
            const Act: string = nz(Button.getAttribute(AppSearchActRules.ActAttr));
            if (Act === AppSearchActRules.More) { this.ToggleMore(Button); return true; }
            // ⛔ Only an action this hit OFFERS runs - a face counts as React (a stale or forged button does nothing).
            let Needs: string = Act;
            if (Act === AppSearchActRules.Emoji) { Needs = AppSearchActRules.React; }
            if (AppSearchActRules.ActsFor(Hit, this.Opts).indexOf(Needs) < 0) { return true; }
            this.Do(Act, Hit, Li, Button);
            return true;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSearchActRun.OnClick");
            return false;
        }
    };

    private Do = (Act: string, Hit: AppSearchHit, Li: ISaEl, Button: ISaEl): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (Act === AppSearchActRules.Reply) { this.Host.Reply(Hit); return; }
            if (Act === AppSearchActRules.Ai) { this.Host.Ai(Hit); return; }
            if (Act === AppSearchActRules.Forward) { this.Host.Forward(Hit); return; }
            if (Act === AppSearchActRules.React) { this.ToggleFaces(Li, Button); return; }
            if (Act === AppSearchActRules.Emoji) { this.React(Hit, Li, nz(Button.getAttribute(AppSearchActRules.EmojiAttr))); return; }
            if (Act === AppSearchActRules.Remind) { this.Remind(Hit, Li); return; }
            if (Act === AppSearchActRules.Copy) { this.Host.Copy(AppSearch.MatchedText(Hit), new AppSearchCopied(Li)); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSearchActRun.Do");
        }
    };

    /** "⋯" only opens (or folds) the hit's row of actions. ⛔ Sends nothing. */
    private ToggleMore = (Button: ISaEl): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Row: ISaEl | null = Button.closest(".sacts");
            if (Row === null) { return; }
            const Open: boolean = Button.getAttribute("aria-expanded") !== "true";
            Button.setAttribute("aria-expanded", String(Open));
            Row.setAttribute(AppSearchActRules.OpenAttr, String(Open));
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSearchActRun.ToggleMore");
        }
    };

    /** React only shows the faces. ⛔ Opening the row sends nothing. */
    private ToggleFaces = (Li: ISaEl, Button: ISaEl): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Faces: ISaEl | null = Li.querySelector(".safaces");
            if (Faces === null) { return; }
            Faces.hidden = !Faces.hidden;
            let Open: string = "false";
            if (Faces.hidden === false) { Open = "true"; }
            Button.setAttribute("aria-expanded", Open);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSearchActRun.ToggleFaces");
        }
    };

    /** ⛔ THE ONE SEND: a face was clicked. One per hit at a time, through the page's single sender. */
    // REUSE-JUSTIFIED: the host's `Why` is a refusal sentence (sending busy, no service) - fate 1, shown in ink on the hit as
    // the chat's reaction row does; the global handler would latch every control for the visit over a normal refusal.
    private React = (Hit: AppSearchHit, Li: ISaEl, Emoji: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Mid: string = nz(Hit.Msg.MessageId);
            if (Mid.length === 0 || Emoji.length === 0 || this.Reacting[Mid] === true) { return; }
            if (AppSearchActRules.OneToOne(Hit.Chat.ChatId) === false) { return; }
            this.Reacting[Mid] = true;
            AppSearchActRun.Say(Li, AppSearchActRules.ReactingWords);
            const Watch: AppSearchReactWatch = new AppSearchReactWatch(this, Li, Mid);
            const Why: string = this.Host.React(Hit, Emoji, Watch, Watch);
            if (Why.length > 0) { this.Done(Mid); AppSearchActRun.Say(Li, Why); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSearchActRun.React");
        }
    };

    // REUSE-JUSTIFIED: the host's `Why` is a refusal sentence (no service, not a 1:1 chat) - fate 1, shown in ink on the hit;
    // the global handler would latch every control for the visit over a normal refusal.
    private Remind = (Hit: AppSearchHit, Li: ISaEl): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Key: string = nz(Hit.Chat.Href) + "|" + nz(Hit.Msg.MessageId) + "|" + Hit.Msg.Minute;
            if (this.Reminding[Key] === true) { return; }
            this.Reminding[Key] = true;
            AppSearchActRun.Say(Li, AppSearchActRules.RemindBusyWords);
            const Why: string = this.Host.Remind(Hit, new AppSearchRemindHeard(this, Li, Key));
            if (Why.length > 0) { delete this.Reminding[Key]; AppSearchActRun.Say(Li, Why); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSearchActRun.Remind");
        }
    };

    /** A reaction settled (or failed): the hit may react again. */
    public Done = (Mid: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try { delete this.Reacting[nz(Mid)]; } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "AppSearchActRun.Done"); }
    };

    public Reminded = (Key: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try { delete this.Reminding[nz(Key)]; } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "AppSearchActRun.Reminded"); }
    };

    public IsReacting = (Mid: string): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try { return this.Reacting[nz(Mid)] === true; } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "AppSearchActRun.IsReacting"); return false; }
    };

    /** The hit's status line, in ink (written only when its words change). */
    public static Say = (Li: ISaEl, Words: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Line: ISaEl | null = Li.querySelector(".sasay");
            if (Line === null || Line.textContent === Words) { return; }
            Line.textContent = Words;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSearchActRun.Say");
        }
    };
}

/** One hit's reaction: `/send`'s answer (`IComposerSent`) and the receipt watch (`IRxPending`, `AppReactPoll`). */
export class AppSearchReactWatch implements IComposerSent, IRxPending {
    private Run: AppSearchActRun;
    private Li: ISaEl;
    private Mid: string;
    private Waiting: boolean = true;

    public constructor(Run: AppSearchActRun, Li: ISaEl, Mid: string) {
        this.Run = Run;
        this.Li = Li;
        this.Mid = nz(Mid);
    }

    public Queued = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try { AppSearchActRun.Say(this.Li, AppSearchActRules.ReactQueuedWords); } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "AppSearchReactWatch.Queued"); }
    };

    // REUSE-JUSTIFIED: `Why` is the service's refusal of the reaction (/send) - fate 1, shown in ink on the hit exactly as
    // the chat's reaction row shows it; the global handler would latch every control for the visit.
    public Failed = (Why: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try { this.End(nz(Why)); } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "AppSearchReactWatch.Failed"); }
    };

    public IsPending = (Mid: string): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try { return this.Waiting && nz(Mid) === this.Mid; } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "AppSearchReactWatch.IsPending"); return false; }
    };

    /** ⚠ "Sent" only on the sender's receipt (`AppReactPoll`). */
    // REUSE-JUSTIFIED: a refused receipt's `Why` is the sender's own sentence - fate 1, shown in ink (as the chat's reaction
    // row does), never the global handler, which would latch every control for the visit.
    public Settle = (Mid: string, Sent: boolean, Unconfirmed: boolean, Why: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (Sent === true) { this.End(AppSearchActRules.ReactSentWords); return; }
            if (Unconfirmed === true) { this.End(AppSearchActRules.ReactUnconfirmedWords); return; }
            this.End(nz(Why));
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSearchReactWatch.Settle");
        }
    };

    public GaveUp = (Mid: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try { this.End(AppSearchActRules.ReactWaitingWords); } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "AppSearchReactWatch.GaveUp"); }
    };

    private End = (Words: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Waiting = false;
            AppSearchActRun.Say(this.Li, Words);
            this.Run.Done(this.Mid);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSearchReactWatch.End");
        }
    };
}

/** Remind me's answer: the task landed, or why not - on the hit. */
export class AppSearchRemindHeard implements IAppTaskHeard {
    public constructor(private Run: AppSearchActRun, private Li: ISaEl, private Key: string) { }

    public Done = (Task: AppRow | null, Changed?: boolean): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try { this.Settle(AppSearchActRules.RemindDoneWords); } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "AppSearchRemindHeard.Done"); }
    };

    // REUSE-JUSTIFIED: `Why` is the /tasks service's own refusal sentence - fate 1, shown in ink on the hit as the Tasks
    // tab does (`AppTaskCall`); the global handler would latch every control for the visit over a normal refusal.
    public Failed = (Why: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try { this.Settle(nz(Why)); } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "AppSearchRemindHeard.Failed"); }
    };

    /** Both answers end the same way: the hit may remind again, and says what happened. */
    private Settle = (Words: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Run.Reminded(this.Key);
            AppSearchActRun.Say(this.Li, Words);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSearchRemindHeard.Settle");
        }
    };
}

/** Copy text's answer, on the hit. ⛔ A refusal is a sentence in ink, never the handler. */
export class AppSearchCopied implements IAssistCopied {
    public constructor(private Li: ISaEl) { }

    public Copied = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try { AppSearchActRun.Say(this.Li, AppSearchActRules.CopiedWords); } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "AppSearchCopied.Copied"); }
    };

    // REUSE-JUSTIFIED: a clipboard the browser refused is the browser's answer the owner can act on (fate 1, ink, as
    // `AppAssistTools` does); routing it to the global handler would latch every control for the visit.
    public Refused = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try { AppSearchActRun.Say(this.Li, AppSearchActRules.CopyRefusedWhy); } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "AppSearchCopied.Refused"); }
    };
}
